import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleClearCache = () => {
    // Clear all localStorage data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.clear();
    
    // Clear sessionStorage as well
    sessionStorage.clear();
    
    setCacheCleared(true);
    setError('');
    
    // Force page refresh after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', formData);
      onLogin(response.data.user, response.data.access_token);
      // Route-level <LoginRedirect /> in App.js honors ?redirect= once user is set.
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <img
                src="/usdol_seal.png"
                alt="U.S. Department of Labor Seal"
                data-testid="login-usdol-seal"
                style={{ width: 84, height: 84, objectFit: 'contain' }}
              />
            </div>
            <h1 className="auth-title">MedCare Apprenticeship Portal</h1>
            <p className="auth-subtitle" style={{ marginBottom: 4 }}>
              U.S. Department of Labor · Workforce Development
            </p>
            <p className="auth-subtitle" style={{ fontSize: '0.78rem', color: '#64748b' }}>
              Sign in to access your online training workspace
            </p>
          </div>

          {error && (
            <div className="alert alert-error" data-testid="error-message">
              {error}
            </div>
          )}

          {cacheCleared && (
            <div style={{ 
              background: '#dcfce7', 
              border: '1px solid #86efac', 
              borderRadius: '8px', 
              padding: '12px', 
              marginBottom: '16px',
              color: '#166534',
              textAlign: 'center'
            }} data-testid="cache-cleared-message">
              ✓ Cache cleared successfully! Refreshing page...
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your password"
                required
                data-testid="password-input"
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-lg" 
              style={{ width: '100%' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>

            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                style={{ fontSize: '0.85rem', color: '#1e3a5f', textDecoration: 'underline' }}
              >
                Forgot password?
              </Link>
            </div>
          </form>

          {/* Clear Cache Button */}
          <div style={{ 
            marginTop: '16px', 
            paddingTop: '16px', 
            borderTop: '1px solid #e5e7eb',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '8px' }}>
              Having trouble seeing admin features or new updates?
            </p>
            <button
              type="button"
              onClick={handleClearCache}
              data-testid="clear-cache-btn"
              style={{
                padding: '10px 20px',
                background: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              🔄 Clear Cache & Re-login
            </button>
          </div>

          <div className="auth-footer">
            Don't have an account? <Link to="/register">Create one</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
